// Raster images only: SVG can carry script, so it is never previewed (same rule as the ITSM).
const PREVIEWABLE_TYPES: readonly string[] = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/bmp"];

export function isPreviewableImage(contentType: string): boolean {
  return PREVIEWABLE_TYPES.includes((contentType ?? "").toLowerCase());
}

export function fileExtension(fileName: string): string {
  const dot: number = fileName.lastIndexOf(".");
  return dot > 0 && dot < fileName.length - 1 ? fileName.slice(dot + 1).toUpperCase() : "FILE";
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** How many files, and how many bytes, a ticket holds (or will hold). */
export interface IHelpdeskFileTotals {
  count: number;
  bytes: number;
}

export const NO_HELPDESK_FILES: IHelpdeskFileTotals = { count: 0, bytes: 0 };

export type HelpdeskFileRejection = "tooLarge" | "tooMany" | "ticketTotal";

export interface IHelpdeskRejectedFile {
  file: File;
  reason: HelpdeskFileRejection;
}

export interface IHelpdeskFileSelection {
  accepted: File[];
  rejected: IHelpdeskRejectedFile[];
}

/** The file limits of the ITSM (contract §4). */
export interface IHelpdeskFileLimits {
  maxFileBytes: number;
  maxFilesPerTicket: number;
  maxTotalBytesPerTicket: number;
}

interface IHelpdeskSizedItem {
  size?: number;
  sizeBytes?: number;
}

export function totalsOf(items: readonly IHelpdeskSizedItem[]): IHelpdeskFileTotals {
  let bytes: number = 0;
  for (const item of items) bytes += item.size ?? item.sizeBytes ?? 0;
  return { count: items.length, bytes };
}

export function addTotals(a: IHelpdeskFileTotals, b: IHelpdeskFileTotals): IHelpdeskFileTotals {
  return { count: a.count + b.count, bytes: a.bytes + b.bytes };
}

/**
 * Splits picked files into the ones the ITSM will take and the ones it would refuse, given what the
 * ticket already holds (`existing`: its attachments plus files already waiting to be sent). A file
 * over the per-file limit is refused on its own; the rest are taken in order until the ticket's
 * file count or total size would be exceeded.
 */
export function selectFiles(
  files: File[],
  limits: IHelpdeskFileLimits,
  existing: IHelpdeskFileTotals
): IHelpdeskFileSelection {
  const accepted: File[] = [];
  const rejected: IHelpdeskRejectedFile[] = [];
  let count: number = existing.count;
  let bytes: number = existing.bytes;
  for (const file of files) {
    if (file.size > limits.maxFileBytes) {
      rejected.push({ file, reason: "tooLarge" });
    } else if (count + 1 > limits.maxFilesPerTicket) {
      rejected.push({ file, reason: "tooMany" });
    } else if (bytes + file.size > limits.maxTotalBytesPerTicket) {
      rejected.push({ file, reason: "ticketTotal" });
    } else {
      accepted.push(file);
      count += 1;
      bytes += file.size;
    }
  }
  return { accepted, rejected };
}

/** Why a picked file was not added, for the warning shown to the user. */
export function describeRejectedFile(rejected: IHelpdeskRejectedFile, limits: IHelpdeskFileLimits): string {
  const name: string = rejected.file.name;
  if (rejected.reason === "tooLarge") return `${name} exceeds the ${formatFileSize(limits.maxFileBytes)} limit.`;
  if (rejected.reason === "tooMany")
    return `${name} was not added: a ticket can hold ${limits.maxFilesPerTicket} files.`;
  return `${name} was not added: a ticket's files can total ${formatFileSize(limits.maxTotalBytesPerTicket)}.`;
}
